if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentProductId = null;
    }

    connectedCallback() {
      // Use a small delay to ensure DOM is fully parsed
      requestAnimationFrame(() => {
        // Check if recommendations are already loaded (prevents flicker on cart updates)
        const recommendationsContainer = this.querySelector('[data-recommendations]');
        const hasContent = recommendationsContainer && recommendationsContainer.children.length > 0;
        const hasCustomRecommendations = this.hasAttribute('data-has-custom-recommendations');
        const foundProduct = this.hasAttribute('data-found-product');
        const productHandle = this.getAttribute('data-product-handle');
        
        // Debug logging
        const originalProductId = this.getAttribute('data-original-product-id');
        const usedProductId = this.getAttribute('data-product-id');
        const wasPrioritized = this.hasAttribute('data-using-prioritized');
        
        console.log('Product Recommendations Init:', {
          originalProductId: originalProductId,
          usedProductId: usedProductId,
          prioritized: wasPrioritized ? '✅ Using product with custom metafields' : 'Using last added',
          hasContent: hasContent,
          contentCount: recommendationsContainer?.children.length,
          hasCustom: hasCustomRecommendations,
          foundProduct: foundProduct,
          productHandle: productHandle
        });
        
        // Store the current product ID from the URL
        this.currentProductId = this.getProductIdFromUrl();
        
        if (!hasContent) {
          // If no custom recommendations, fetch automatic ones
          if (!hasCustomRecommendations) {
            console.log('Fetching automatic recommendations...');
            this.performRecommendations();
          } else {
            // Has custom recommendations but no content yet - might be loading
            // This shouldn't happen if server-side rendering worked
            console.warn('Custom recommendations flag set but no content found', this);
          }
        } else {
          // Already loaded from server-side render, just initialize interactive features
          console.log('Initializing from server-rendered content');
          this.initSlider();
          this.initAddToCartForms();
        }
      });
    }
    
    getProductIdFromUrl() {
      const url = this.dataset.url;
      const match = url.match(/product_id=(\d+)/);
      return match ? match[1] : null;
    }

    disconnectedCallback() {
      // Clean up slider when element is removed
      if (this.slider && this.slider.destroy) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    performRecommendations(forceRefresh = false) {
      const recommendations = this.querySelector(
        '[data-recommendations]'
      );
      if (!recommendations) return;
      
      // Check if this product has custom recommendations
      const hasCustomRecommendations = this.hasAttribute('data-has-custom-recommendations');
      
      // Update current product ID from URL
      const newProductId = this.getProductIdFromUrl();
      
      // Only fetch if forced or if product ID changed
      // Don't fetch if we already have custom recommendations (unless forced)
      if (!forceRefresh && hasCustomRecommendations && recommendations.children.length > 0) {
        return;
      }
      
      if (!forceRefresh && this.currentProductId === newProductId && recommendations.children.length > 0) {
        return;
      }
      
      this.currentProductId = newProductId;
      
      fetch(this.dataset.url)
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          
          const recommendationsHTML = html.querySelector('[data-recommendations]')?.innerHTML;
          if (!recommendationsHTML) return;
          
          // Check if the fetched content has custom recommendations
          const fetchedElement = html.querySelector('product-recommendations');
          const hasCustom = fetchedElement?.hasAttribute('data-has-custom-recommendations');
          
          // Update our attribute to match
          if (hasCustom) {
            this.setAttribute('data-has-custom-recommendations', 'true');
          } else {
            this.removeAttribute('data-has-custom-recommendations');
          }
          
          this.classList.remove('hidden');
          var cartDrawerItemsFull = document.querySelector('.cart-drawer-items__full');
          if(cartDrawerItemsFull) {
            cartDrawerItemsFull.classList.remove('cart-drawer-items__full');
          }
          recommendations.innerHTML = recommendationsHTML;
          this.initSlider();
          this.initAddToCartForms();

          // initiate quick cart after fetch
          // to activate trigger button events
          setTimeout(() => {
            if (document.querySelector("quick-cart-drawer")) {
              document.querySelector("quick-cart-drawer").init();
            } else {
              // if quick-cart-drawer does not exists, remove trigger buttons
              this.querySelectorAll(".quick-cart-drawer__trigger").forEach(el => {
                el?.remove();
              })
            }
          }, 500);

        });
    }
    
    // Method to update recommendations for a new product
    updateForProduct(productId) {
      if (!productId) return;
      
      // Update the data-url with the new product ID
      const currentUrl = this.dataset.url;
      const newUrl = currentUrl.replace(/product_id=\d+/, `product_id=${productId}`);
      this.dataset.url = newUrl;
      
      // Fetch new recommendations
      this.performRecommendations(true);
    }

    initSlider() {
      // Destroy existing slider if it exists
      if (this.slider && this.slider.destroy) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
      
      const swiperElement = this.querySelector('.swiper');
      if (swiperElement) {
        this.slider = new Swiper(swiperElement, {
          slidesPerView: 'auto',
          spaceBetween: 16,
        });
      }
    }

    initAddToCartForms() {
      const forms = this.querySelectorAll('.product-recommendations__form');
      forms.forEach(form => {
        // Remove existing listener if any to prevent duplicates
        const oldHandler = form._submitHandler;
        if (oldHandler) {
          form.removeEventListener('submit', oldHandler);
        }
        
        // Create and store new handler
        const newHandler = this.handleAddToCart.bind(this);
        form._submitHandler = newHandler;
        form.addEventListener('submit', newHandler);
        
        // Also attach to button click as a fallback
        const button = form.querySelector('button[type="submit"]');
        if (button) {
          const oldClickHandler = button._clickHandler;
          if (oldClickHandler) {
            button.removeEventListener('click', oldClickHandler);
          }
          
          const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Trigger form submit which will be handled by our submit handler
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          };
          button._clickHandler = clickHandler;
          button.addEventListener('click', clickHandler);
        }
      });
    }

    handleAddToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const form = event.target;
      const button = form.querySelector('button[type="submit"]');
      const buttonText = button.querySelector('span');
      const loadingSpinner = button.querySelector('.product-recommendations__loading');
      
      // Disable button and show loading state
      button.setAttribute('disabled', '');
      button.classList.add('is-loading');
      if (loadingSpinner) loadingSpinner.style.display = 'block';
      
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      
      const formData = new FormData(form);
      
      // Get cart drawer or cart notification element
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cart = cartDrawer || cartNotification;
      
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map(section => section.section)
        );
        formData.append('sections_url', window.location.pathname);
      }
      
      config.body = formData;
      
      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            this.handleError(response, button, buttonText);
            return;
          }
          
          if (cart) {
            cart.renderContents(response);
          }
          
          // Update cart counters
          if (typeof updateCartCounters === 'function') {
            updateCartCounters();
          }
          
          // Update free shipping bar if exists
          if (typeof updateFreeShipping === 'function') {
            updateFreeShipping();
          }
          
          // Show success feedback
          if (buttonText) {
            const originalText = buttonText.textContent;
            buttonText.textContent = '✓ Added!';
            setTimeout(() => {
              buttonText.textContent = originalText;
            }, 2000);
          }
        })
        .catch(error => {
          console.error('Error:', error);
          this.handleError({ message: 'An error occurred' }, button, buttonText);
        })
        .finally(() => {
          // Re-enable button and hide loading state
          button.removeAttribute('disabled');
          button.classList.remove('is-loading');
          if (loadingSpinner) loadingSpinner.style.display = 'none';
        });
    }

    handleError(response, button, buttonText) {
      console.error('Add to cart error:', response);
      if (buttonText) {
        const originalText = buttonText.textContent;
        buttonText.textContent = '✗ Error';
        setTimeout(() => {
          buttonText.textContent = originalText;
        }, 2000);
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
}
